import React, { useEffect, useRef, useState } from 'react';
import EnhancedTable from '../../components/table';
import Status from '../../components/status';
import Config from '../../config';
import Moment from 'react-moment';
import Helpers, { DocumentHelper, documentSignalrUpdate, getCompanyNames, getStatusOptions } from '../../helpers';
import { withRouter } from 'react-router';
import DocLink from '../../components/document_link';
import DocType from '../../components/document_type';
import { Button, Dialog, Toolbar, DialogContent, DialogActions, FormControlLabel, Radio, RadioGroup, Select, MenuItem, Typography, List, ListItem as ListItemBase, ListItemText } from '@mui/material';
import isEqual from 'lodash.isequal';
import DownloadButton from '../../components/download_button';
import axios from 'axios';
import FileSaverBase from 'filesaver.js-npm';
import { Redirect } from 'react-router-dom';
import { loadProfiles, getDownloadLink } from '../files/export';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { nullableDateTemplate } from '../../helpers';

// ListItem's legacy `button` prop isn't part of the typed API in this MUI
// version; cast to any at the import boundary (same pattern used elsewhere
// in this migration, e.g. settings/v2/scheduled_tasks.tsx).
const ListItem: any = ListItemBase;
// filesaver.js-npm ships no type declarations (matches reports.tsx/
// scorecards.tsx's cast).
const FileSaver: any = FileSaverBase;

const docTypes: { [key: string]: string } = {
    order: "Order",
    ack: "Acknowledgement",
    shipment: "Shipment",
    invoice: "Invoice",
    return: "Return"
}
const typeKeywords: { [key: string]: string } = {
    "order": docTypes.order,
    "shipment": docTypes.shipment,
    "advanceshipmentnotice": docTypes.shipment,
    "asn": docTypes.shipment,
    "invoice": docTypes.invoice,
    "return": docTypes.return,
    "rma": docTypes.return,
    "acknowledgement": docTypes.ack,
    "poack": docTypes.ack,
    "ack": docTypes.ack
};

// Was the class instance field `this.types`: a constant alias map, so it lives
// at module scope now.
const documentTypeAliases: { [key: string]: string } = {
    "AdvanceShipmentNotice": "Shipment",
    "POAck": "Acknowledgement"
};

type SearchProps = {
    setTitle?: (title: string) => void;
    signalr?: any;
};

type SearchState = {
    term?: string;
    title: string;
    selectedOrders: boolean;
    selectedShipments: boolean;
    disableRetry: boolean;
    showBulkRetry: boolean;
    showError: boolean;
    showExport: boolean;
    selectedTypes: string[];
    disableExport: boolean;
    selectedProfile: string;
    selectedFileType: string;
    showBulkActions: boolean;
    typeCounts: { [key: string]: number };
    ready?: boolean;
    mounted?: boolean;
    filter?: any;
    redirect?: string;
    selectedFailed?: number;
    selectedDocumentType?: string;
    bulkStatusAction?: string;
    bulkStatusCallback?: () => void;
    bulkStatusTarget?: number;
    error?: string;
};

const Search = (props: SearchProps) => {
    const [state, setStateRaw] = useState<SearchState>({
        term: new Helpers().queryString()['term'], title: "Search Results", selectedOrders: false,
        selectedShipments: false, disableRetry: false, showBulkRetry: false, showError: false, showExport: false,
        selectedTypes: [], disableExport: false, selectedProfile: '', selectedFileType: 'csv', showBulkActions: false,
        typeCounts: {}
    });
    // Emulates class setState: partial merge (object or updater fn).
    const setState = (update: Partial<SearchState> | ((prev: SearchState) => Partial<SearchState>)) => {
        setStateRaw(prev => ({ ...prev, ...(typeof update === 'function' ? (update as (p: SearchState) => Partial<SearchState>)(prev) : update) }));
    };

    // Ad hoc instance fields (not part of React state) populated by the table's
    // setSelected callback / lazily-loaded export profiles.
    const selectedRef = useRef<any[]>([]);
    const profilesRef = useRef<any[] | undefined>(undefined);

    const removeFilterKeywords = (str?: string | null) => {
        var keywords = Object.keys(typeKeywords);
        if (typeof str === 'undefined' || str === null || str === '') {
            return null;
        }
        var str2 = "";
        var split = str.split(" ");
        for (var i = 0; i < split.length; i++) {
            var j = keywords.length;
            var strLower = split[i].toLowerCase().trim();
            while (j--) {
                if (keywords[j] === strLower || strLower === keywords[j] + "s") {
                    break;
                }
            }
            if (j === -1) {
                str2 += split[i] + " ";
            }
        }
        return str2.trim();
    };
    const createFilter = (str?: string | null) => {
        var filters: any[] = [];
        if (str == null || str === '') {
            return null;
        }
        var split = str.split(" ");
        var findStr = (value: string) => (r: string) => r === value || (r + "s") === value;
        for (var i = 0; i < split.length; i++) {
            var str2 = split[i];
            if (str2 === "") {
                continue;
            }
            var lowerStr2 = str2.toLowerCase();
            if (isNaN(str2 as any)) {
                let match = Object.keys(typeKeywords).find(findStr(lowerStr2));
                if(match){
                    filters.push({ field: 'documentType', operator: "eq", value: typeKeywords[match] });
                }
            }
        }
        if (filters.length === 0) {
            return undefined;
        }
        return {filters: filters};
    };

    // componentDidMount
    useEffect(() => {
        setState({mounted: true});
        if(props.setTitle){
            props.setTitle('Search');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentDidUpdate: keep term/title/filter in sync with the URL query
    // string on every render (runs after each commit, guarded so it only
    // updates state when a value actually changed -- mirrors the original
    // class lifecycle).
    useEffect(() => {
        var term = new Helpers().queryString()['term'];
        var newState: any = null;
        if(state.term !== term){
            newState = {term: term};
        }
        var title = new Helpers().queryString()['title'];
        var searchTitle = title ? `Search Results: ${title}` : 'Search Results';
        if(state.title !== searchTitle){
            newState = {title: searchTitle, ...newState};
        }
        var filter = new Helpers().queryString()['filter'];
        if(filter){
            var decodedFilter = JSON.parse(decodeURIComponent(escape(window.atob(filter.replace(/_/g, "=")))));
            if(!isEqual(decodedFilter, state.filter)){
                newState = {filter: decodedFilter, ...newState};
            }
        }
        if(!filter && state.filter){
            newState = {filter: null, ...newState};
        }
        if(newState){
            setState({...newState});
        }
        if(!state.ready){
            setState({ready: true});
        }
        if(props.setTitle){
            props.setTitle('Search');
        }
    });

    const downloadLabels = async () => {
        var shipments = selectedRef.current.filter(r => r.documentType === 'AdvanceShipmentNotice').map(r => r.id);
        if(shipments.length === 0){
            return;
        }
        var link = (await axios.get(Config.api + `/api/v1/shipments/shippinglabel?logicbrokerkeys=${shipments.join(',')}&filetype=pdf`)).data.Body;
        var res = (await axios({url: link, method: 'GET', responseType: 'blob'})).data;
        var blob = new Blob([res], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, `shippinglabels-${new Date().getTime()}.pdf`);
    };
    const downloadPackingSlips = async () => {
        let shipments = selectedRef.current.filter(r => r.documentType === 'AdvanceShipmentNotice').map(r => r.id);
        let orders = selectedRef.current.filter(r => r.documentType === 'Order').map(r => r.id);
        if(orders.length !== 0){
            let link = (await axios.get(Config.api + `/api/v1/orders/picklist?logicbrokerkeys=${orders.join(',')}&filetype=pdf`)).data.Body;
            let res = (await axios({url: link, method: 'GET', responseType: 'blob'})).data;
            let blob = new Blob([res], { type: "application/octet-stream" });
            FileSaver.saveAs(blob, `order-packingslips-${new Date().getTime()}.pdf`);
        }
        if(shipments.length !== 0){
            let asnLink = (await axios.get(Config.api + `/api/v1/shipments/packingslip?logicbrokerkeys=${shipments.join(',')}&filetype=pdf`)).data.Body;
            let asnRes = (await axios({url: asnLink, method: 'GET', responseType: 'blob'})).data;
            let asnBlob = new Blob([asnRes], { type: "application/octet-stream" });
            FileSaver.saveAs(asnBlob, `shipment-packingslips-${new Date().getTime()}.pdf`);
        }
    };
    const invoiceSelected = () => {
        let orders = selectedRef.current.filter(r => r.documentType === "Order").map(r => r.id);
        let shipments = selectedRef.current.filter(r => r.documentType === "AdvanceShipmentNotice").map(r => r.id);
        setState({redirect: '/order-management/invoice?orderids=' + orders.join(',') + "&shipmentids=" + shipments.join(',')});
    };
    const fulfillSelected = () => {
        let orders = selectedRef.current.filter(r => r.documentType === "Order").map(r => r.id);
        setState({redirect: '/order-management/fulfill-cancel?orderids=' + orders.join(',')});
    };
    const ackSelected = () => {
        let orders = selectedRef.current.filter(r => r.documentType === "Order").map(r => r.id);
        setState({redirect: '/order-management/accept?orderids=' + orders.join(',')});
    };
    const showBulkRetry = () => {
        setState({showBulkRetry: true, disableRetry: false, bulkStatusAction: 'Retry', bulkStatusCallback: () => retry(0), bulkStatusTarget: state.selectedFailed});
    };
    const showBulkIgnore = () => {
        setState({showBulkActions: false, showBulkRetry: true, disableRetry: false, bulkStatusAction: 'Ignore', bulkStatusCallback: () => retry(1400), bulkStatusTarget: state.selectedFailed});
    };
    const showExport = async () => {
        if(!profilesRef.current){
            profilesRef.current = await loadProfiles();
        }
        var selectedProfile = state.selectedProfile;
        if(state.selectedDocumentType !== ''){
            if(profilesRef.current.filter(r => r.DocumentType === state.selectedDocumentType && r.Name === selectedProfile).length === 0){
                selectedProfile = profilesRef.current.filter(r => r.DocumentType === state.selectedDocumentType)[0].Name;
            }
        } else {
            selectedProfile = '';
        }
        setState({showExport: true, disableExport: false, selectedProfile});
    };
    const download = async () => {
        setState({disableExport: true});
        let profile = (profilesRef.current || []).find(r => r.Name === state.selectedProfile);
        let fileType = state.selectedFileType || 'csv';
        let lbKeys = selectedRef.current.filter(r => getDocumentType(r.documentType) === state.selectedDocumentType).map(r => r.internalId);
        try{
            let link = await getDownloadLink(profile.DocumentType, null, profile.Fields, fileType, lbKeys);
            return {href: link, fileName: 'export.' + fileType };
        }catch(ex: any){
            setState({showError: true, error: ex.message});
        }
    };
    const retry = async (status: number) => {
        setState({disableRetry: true});
        var documentsToRetry = selectedRef.current.filter(r => r.status === 1200);
        await new DocumentHelper().bulkUpdateDocuments(documentsToRetry, status);
        setState({showBulkRetry: false});
    };
    const showBulkActions = () => setState({showBulkActions: true});
    const getDocumentType = (docType: string) => documentTypeAliases[docType] ? documentTypeAliases[docType] : docType;
    const arrayEquals = (array1: any[], array2: any[]) => array1.length === array2.length && array1.every(function(value, index) { return value === array2[index]});

    if(state.redirect){
        return <Redirect to={state.redirect} push={true}/>
    }
    var config: any = {
        url: Config.api + `/odata/Company/Functions.DocumentSearch`,
        columns: [
          { id: "id", hidden: true, width: '5em'},
          { id: 'status', hidden: true, label: 'Status Code', type: 'number' },
          { id: 'internalId', type: 'number', filterable: true, sortable: true, numeric: true, disablePadding: true, label: 'ID#',width: '5em' },
          { id: 'partnerPo', filterable: true, label: 'Reference Number',width: '9em' },
          { id: 'sourceKey', filterable: true, label: 'Source Key', hidden: true, toggleable: true },
          { id: 'linkKey', filterable: true, hidden: true, toggleable: true, label: 'Link Key' },
          { id: 'date', type:'date', filterable: true, sortable: true, label: 'Date', template: (value: any) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '11em' },
          { id: 'requestedShipDate', type:'localdate', filterable: true, sortable: true, label: 'Requested Ship Date', template: nullableDateTemplate, width: '11em', hidden: true, toggleable: true },
          { id: 'statusChangeDate', type:'localdate', hidden: true,width: '11em', label: 'Status Change Date', template: nullableDateTemplate },
          { id: 'documentType', filterable: true, label: 'Type', width: '10em', template: (value: any) => <DocType docType={value}/> },
          { id: 'companyName', filterable: true, label: 'Sender',width: '15em', toggleable: true, getOptions: getCompanyNames },
          { id: 'partnerCompanyName', filterable: true, label: 'Receiver',width: '15em', toggleable: true, getOptions: getCompanyNames },
          { id: 'statusDescription', filterable: true, label: 'Status', getOptions: getStatusOptions, template: (value: any, row: any, addFilter: any) => (<Status description={value} onClick={() => addFilter(isNaN(value) ? {operator: 'eq', value: value, field: 'statusDescription'} : {operator: 'eq', value: row.status, field: 'status'} )}/>), width: '14em', stopPropagation: true },
          { id: 'subStatusDescription', filterable: true, hidden: true, toggleable: true, label: 'SubStatus' },
          { id: 'source', filterable: true, hidden: true, toggleable: true, label: 'System' },
          { command: 'commands', stopPropagation: 'true', width: '6em', overflow: true, template: (value: any, row: any) => (
              <Button size='small' variant='outlined' component={DocLink} docType={row.documentType} id={row.id}>View</Button>
          )}
      ],
      setSelected: (ids: any[], models: any[]) => {
          selectedRef.current = models;
          let selectedOrders = models.filter(r => r.documentType === 'Order').length !== 0;
          let selectedShipments = models.filter(r => r.documentType === 'AdvanceShipmentNotice').length !== 0;
          let selectedFailed = models.filter(r => r.status === 1200).length;
          let selectedTypes: string[] = [];
          let typeCounts: { [key: string]: number } = {};
          models.forEach(m => {
              if(selectedTypes.indexOf(m.documentType) === -1){
                  selectedTypes.push(m.documentType);
              }
              typeCounts[m.documentType] = typeCounts[m.documentType] ? typeCounts[m.documentType] + 1 : 1;
          });
          selectedTypes = selectedTypes.map(r => getDocumentType(r)).sort();
          if(state.selectedOrders !== selectedOrders || state.selectedFailed !== selectedFailed
              || state.selectedShipments !== selectedShipments || !arrayEquals(selectedTypes, state.selectedTypes)
              || !isEqual(typeCounts, state.typeCounts)
          ){
              // Use a functional update so selectedProfile (and the current
              // document-type selection) are read from the latest committed
              // state rather than this callback's closure, which can be stale
              // if the export dialog changed the profile after this config was
              // built. The class read this.state.selectedProfile fresh here; a
              // plain setState({..., selectedProfile: state.selectedProfile})
              // clobbers a profile the user just picked.
              setState(prev => {
                  let selectedType = selectedTypes.indexOf(prev.selectedDocumentType || '') > -1 ? prev.selectedDocumentType : '';
                  if(selectedType === '' && selectedTypes.length > 0){
                      selectedType = selectedTypes[0];
                  }
                  return {selectedOrders, selectedShipments, selectedTypes, typeCounts, selectedDocumentType: selectedType, selectedProfile: prev.selectedProfile, selectedFailed};
              });
          }
      },
      filterInUrl: true,
      selectable: true,
      selectedActions: <div style={{display: 'flex'}}>
        <Button disabled={!state.selectedFailed} onClick={showBulkRetry}>Retry</Button>
        <Button onClick={showExport}>Export</Button>
        <Button disabled={!state.selectedOrders && !state.selectedShipments && !state.selectedFailed} onClick={showBulkActions}>Bulk Actions</Button>
        <DownloadButton disabled={!state.selectedShipments} onClick={downloadLabels} style={{width: '14rem'}}>Download GS1 Labels</DownloadButton>
        <DownloadButton disabled={!state.selectedOrders && !state.selectedShipments} onClick={downloadPackingSlips} style={{width: '14rem'}}>Download Packing Slips</DownloadButton>
      </div>,
      order: 'desc',
      orderBy: 'internalId',
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
      signalr: props.signalr,
      onSignalrUpdate: documentSignalrUpdate,
      term: removeFilterKeywords(state.term),
      filter: state.filter || createFilter(state.term),
      title: state.title
    }
    return <>
    {state.ready ? <EnhancedTable config={config}/> : ''}
    <Dialog maxWidth="sm" fullWidth open={state.showExport} onClose={() => {setState({showExport: false})}}>
        <Toolbar className='lbtoolbar'>{'Export'}</Toolbar>
        <DialogContent>
        Document type
        <Select style={{marginTop: "10px", marginBottom: "10px", width: "100%"}} displayEmpty value={state.selectedDocumentType} onChange={(event: any) => setState({selectedDocumentType: event.target.value, selectedProfile: (profilesRef.current || []).filter(r => r.DocumentType === event.target.value)[0].Name})}>
        {state.selectedTypes.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </Select>
        Export profile
        {profilesRef.current && <Select style={{marginTop: "10px", marginBottom: "10px", width: "100%"}} displayEmpty value={state.selectedProfile} onChange={(event: any) => setState({selectedProfile: event.target.value})}>
        {profilesRef.current.filter(r => r.DocumentType === state.selectedDocumentType).map(r => <MenuItem key={r.Name} value={r.Name}>{r.Name}</MenuItem>)}
        </Select>}
        File type
        <RadioGroup style={{flexDirection: 'column'}} value={state.selectedFileType} onChange={(e: any) => setState({selectedFileType: e.target.value})}>
          <FormControlLabel value='csv' control={<Radio />} label='CSV'/>
          <FormControlLabel value='xlsx' control={<Radio />} label='XLSX'/>
        </RadioGroup>
        </DialogContent>
        <DialogActions>
          <DownloadButton variant='text' onError={(e: any) => setState({showError: true, error: e, disableExport: false})} color='primary' handleComplete={() => {setState({disableExport: false})}} disabled={state.disableExport || !state.selectedFileType || !state.selectedProfile} onClick={download}>Download</DownloadButton>
        </DialogActions>
    </Dialog>
    <Dialog maxWidth="sm" fullWidth open={state.showBulkRetry} onClose={() => {setState({showBulkRetry: false})}}>
        <Toolbar className='lbtoolbar'>{state.bulkStatusAction}</Toolbar>
        <DialogContent>
            <Typography>
            {state.selectedFailed === 1 ? `Are you sure you want to ${(state.bulkStatusAction || '').toLowerCase()} this failed document?` : `Are you sure you want to ${(state.bulkStatusAction || '').toLowerCase()} these ${state.bulkStatusTarget} failed documents?`}
            </Typography>
        </DialogContent>
        <DialogActions>
        <DialogActions>
          <Button variant='text' disabled={state.disableRetry} onClick={() => setState({showBulkRetry: false})} color="primary">
            No
          </Button>
          <Button variant='text' disabled={state.disableRetry} onClick={state.bulkStatusCallback} color="primary">
            Yes
          </Button>
        </DialogActions>
        </DialogActions>
    </Dialog>
    <Dialog open={state.showError} onClose={() => {setState({showError: false})}}>
        <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
        <DialogContent><Typography>{state.error}</Typography></DialogContent>
    </Dialog>
    <Dialog maxWidth='xs' fullWidth open={state.showBulkActions} onClose={() => {setState({showBulkActions: false})}}>
        <Toolbar className='lbtoolbar'>{'Bulk Document Actions'}</Toolbar>
        <List style={{padding: 0}}>
            <ListItem button disabled={!state.selectedOrders} onClick={ackSelected}>
                <ListItemText primary="Accept/Reject" secondary={`${state.typeCounts.Order || 0} selected orders`} />
                <ArrowForwardIosIcon color="action"/>
            </ListItem>
            <ListItem button disabled={!state.selectedOrders} onClick={fulfillSelected}>
                <ListItemText primary="Ship/Cancel" secondary={`${state.typeCounts.Order || 0} selected orders`} />
                <ArrowForwardIosIcon color="action"/>
            </ListItem>
            <ListItem button disabled={!state.selectedOrders && !state.selectedShipments} onClick={invoiceSelected}>
                <ListItemText primary="Invoice" secondary={`${state.typeCounts.Order || 0} selected orders, ${state.typeCounts.AdvanceShipmentNotice || 0} selected shipments`} />
                <ArrowForwardIosIcon color="action"/>
            </ListItem>
            <ListItem button disabled={!state.selectedFailed} onClick={showBulkIgnore}>
                <ListItemText primary="Ignore" secondary={`${state.selectedFailed} failed documents`} />
                <ArrowForwardIosIcon color="action"/>
            </ListItem>
        </List>
    </Dialog>
    </>;
};

// withRouter(...) infers empty props here (react-router v5 strips the custom
// SearchProps), so App.tsx passing setTitle/signalr trips TS2322 once App is
// also typed. Cast the wrapped export to any at this boundary — the same
// boundary-cast style used for the untyped imports above. Integration-only:
// on feat/LB-299 alone App is still .js and never typechecks this call.
export default withRouter(Search as any) as any;
